import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import * as XLSX from 'xlsx';
import { DataService } from 'src/app/data.service';

/**
 *  CSOPORTTAGSÁG-IMPORT — „csoportba járó tanulók" lista beolvasása.
 *
 *  A csoportokba a tanulókat eddig kézzel, egyesével kellett bepipálni, pedig a lista
 *  letölthető abból a rendszerből, ahol a beosztás készül. Több száz sornál ez nem
 *  csak lassú: menet közben el is csúszik, és utólag nem lehet megmondani, mi maradt ki.
 *
 *  A képernyő KÉT LÉPÉS, és az első nem ír semmit: előbb megmutatja, mit talált és mit
 *  lehet vele kezdeni, és csak azt hajtja végre, amit csoportonként jóváhagytak.
 */
@Component({
  selector: 'app-admin-importcsoporttagsag',
  templateUrl: './importcsoporttagsag.component.html',
  styleUrls: ['./importcsoporttagsag.component.scss'],
})
export class AdminImportCsoporttagsagComponent implements OnInit {
  intezmeny = '';
  fajlNev = '';
  fejlecek: string[] = [];
  nyersSorok: any[] = [];

  /** Oszlop-hozzárendelés: melyik fejléc mit jelent. */
  oszlop = { csoport: '', oktazon: '', nev: '', osztaly: '' };

  elemzes: any = null;
  elemzesFut = false;
  fut = false;
  eredmeny: any = null;

  /** Csoportonkénti döntés kulcs szerint: { muvelet, cel_id }. */
  dontes: Record<string, any> = {};
  /** A megnyitott csoport részletei (melyik sor miért nem megy). */
  nyitott: Record<string, boolean> = {};

  /*  SZINKRON MÓD. Alapból az import csak hozzáad: aki a KRÉTÁ-ban átkerült egy másik
   *  csoportba, az nálunk mindkét névsorban benne maradt. Bekapcsolva a fájl a csoport
   *  TELJES névsora – aki nincs benne, annak a tagsága lezárul. Tudatos döntés, ezért
   *  alapból ki van kapcsolva. */
  szinkron = false;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const si = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
  }

  get intezmenyPath(): string { return encodeURI(this.intezmeny); }

  /* ── Fájl ────────────────────────────────────────────────────────────────
   *  A fejléceket nem találgatjuk vakon: felismerjük a szokásos elnevezéseket,
   *  de a hozzárendelés látszik és átállítható – egy ismeretlen fejlécű exporttal
   *  se legyen zsákutca a képernyő. */
  private static readonly CSOPORT_NEVEK = ['csoport', 'csoport neve', 'csoportnév', 'csoport név', 'osztálycsoport', 'foglalkozás'];
  private static readonly OKTAZON_NEVEK = ['oktatási azonosító', 'oktatasi azonosito', 'oktatási azonosító szám', 'oktazon', 'om azonosító', 'tanulói azonosító', 'diákigazolvány szám'];
  private static readonly NEV_NEVEK = ['név', 'nev', 'tanuló', 'tanulo', 'tanuló neve', 'diák', 'nyomtatási név', 'viselt név'];
  private static readonly OSZTALY_NEVEK = ['osztály', 'osztaly', 'osztály neve'];

  fajlValtozott(event: any) {
    const file = event?.target?.files?.[0];
    if (!file) return;
    this.fajlNev = file.name;
    this.elemzes = null;
    this.eredmeny = null;
    const olvaso = new FileReader();
    olvaso.onload = (e: any) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary', cellDates: true });
        const lap = wb.Sheets[wb.SheetNames[0]];
        const sorok: any[] = XLSX.utils.sheet_to_json(lap, { raw: false, defval: null });
        this.nyersSorok = sorok;
        this.fejlecek = sorok.length ? Object.keys(sorok[0]) : [];
        this.oszlopokFelismer();
        if (!sorok.length) this.data.sendToast('A fájl üres.', 'warning');
      } catch {
        this.data.sendToast('A fájlt nem sikerült beolvasni. Excel (.xlsx/.xls) vagy CSV kell.', 'error');
        this.nyersSorok = []; this.fejlecek = [];
      }
    };
    olvaso.readAsBinaryString(file);
  }

  private oszlopokFelismer() {
    const talal = (jeloltek: string[]) => this.fejlecek.find((f) =>
      jeloltek.includes(String(f).trim().toLowerCase()))
      || this.fejlecek.find((f) => jeloltek.some((j) => String(f).trim().toLowerCase().includes(j)))
      || '';
    this.oszlop = {
      csoport: talal(AdminImportCsoporttagsagComponent.CSOPORT_NEVEK),
      oktazon: talal(AdminImportCsoporttagsagComponent.OKTAZON_NEVEK),
      nev: talal(AdminImportCsoporttagsagComponent.NEV_NEVEK),
      osztaly: talal(AdminImportCsoporttagsagComponent.OSZTALY_NEVEK),
    };
  }

  get kuldhetoSorok(): any[] {
    if (!this.oszlop.csoport) return [];
    return (this.nyersSorok || []).map((r) => ({
      csoport: String(r[this.oszlop.csoport] ?? '').trim(),
      oktazon: this.oszlop.oktazon ? String(r[this.oszlop.oktazon] ?? '').trim() : '',
      nev: this.oszlop.nev ? String(r[this.oszlop.nev] ?? '').trim() : '',
      osztaly: this.oszlop.osztaly ? String(r[this.oszlop.osztaly] ?? '').trim() : '',
    })).filter((r) => r.csoport && (r.oktazon || r.nev));
  }

  /* ── Elemzés ─────────────────────────────────────────────────────────── */

  async elemez() {
    const sorok = this.kuldhetoSorok;
    if (!sorok.length) {
      this.data.sendToast('Nincs feldolgozható sor — állítsd be a csoport és a tanuló oszlopát.', 'warning');
      return;
    }
    this.elemzesFut = true;
    this.eredmeny = null;
    try {
      this.elemzes = await firstValueFrom(this.http.post(
        `/api/import-csoporttagsag/elemzes/${this.intezmenyPath}`, { sorok }));
      this.dontes = {};
      for (const cs of this.elemzes?.csoportok || []) {
        // Meglévő csoportnál az alapértelmezés a felvétel; újnál semmi – csoportot
        // létrehozni tudatos döntés, nem lehet véletlen mellékhatás.
        this.dontes[this.kulcs(cs)] = cs.letezo
          ? { muvelet: 'meglevo', cel_id: cs.letezo.id }
          : { muvelet: 'kihagy', cel_id: '' };
      }
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az elemzés nem sikerült.', 'error');
      this.elemzes = null;
    } finally {
      this.elemzesFut = false;
    }
  }

  /*  A kulcs ugyanaz, amit a kiszolgáló számol: kisbetűs, ékezet és írásjel nélkül.
   *  Így a „Napközi 1z" és a „napközi 1.z" ugyanaz a csoport marad. */
  kulcs(cs: any): string {
    return String(cs?.nev || '').toLowerCase().normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '');
  }

  muveletValt(cs: any, muvelet: string) {
    const k = this.kulcs(cs);
    const d = this.dontes[k] || {};
    d.muvelet = muvelet;
    if (muvelet === 'meglevo' && !d.cel_id) d.cel_id = cs.letezo?.id || cs.hasonlok?.[0]?.id || '';
    if (muvelet !== 'meglevo') d.cel_id = '';
    this.dontes[k] = d;
  }

  /** Hány tagság jönne létre a mostani döntésekkel. */
  get vartUjTagsag(): number {
    return (this.elemzes?.csoportok || []).reduce((a: number, cs: any) => {
      const d = this.dontes[this.kulcs(cs)] || {};
      if (d.muvelet === 'kihagy' || (d.muvelet === 'meglevo' && !d.cel_id)) return a;
      return a + cs.felveheto;
    }, 0);
  }
  get vartUjCsoport(): number {
    return (this.elemzes?.csoportok || [])
      .filter((cs: any) => this.dontes[this.kulcs(cs)]?.muvelet === 'letrehoz').length;
  }

  /*  Hány tagság zárulna le szinkron módban. Az előnézet a MEGLÉVŐ, azonos nevű csoportra
   *  szól; ha máshová irányítod a sorokat, a kiszolgáló a végrehajtáskor arra számolja. */
  kikerulE(cs: any): number {
    const d = this.dontes[this.kulcs(cs)] || {};
    if (d.muvelet !== 'meglevo' || !d.cel_id || cs.hibas) return 0;
    return d.cel_id === cs.letezo?.id ? (cs.kikerulne || 0) : 0;
  }
  get vartLezaras(): number {
    return (this.elemzes?.csoportok || []).reduce((a: number, cs: any) => a + this.kikerulE(cs), 0);
  }
  /** Szinkronizálható csoport: meglévőbe megy, és minden sora azonosítható. */
  get szinkronCsoportok(): number {
    return (this.elemzes?.csoportok || []).filter((cs: any) => {
      const d = this.dontes[this.kulcs(cs)] || {};
      return d.muvelet === 'meglevo' && !!d.cel_id && !cs.hibas;
    }).length;
  }
  get vegrehajthato(): boolean {
    return !!(this.vartUjTagsag || this.vartUjCsoport || (this.szinkron && this.szinkronCsoportok));
  }

  allapotSzoveg(a: string): string {
    return ({
      'ok': 'felvehető',
      'mar-tagja': 'már tagja — kimarad',
      'fajlban-ismetlodik': 'a fájlban ismétlődik — egyszer megy be',
      'ismeretlen-azonosito': 'ilyen oktatási azonosító nincs',
      'ismeretlen-nev': 'ilyen nevű tanuló nincs',
      'tobb-azonos-nev': 'több tanulóra is illik a név — nem tippelünk',
    } as any)[a] || a;
  }

  /** A „Kész" blokk soraihoz: csak ahol történt valami, vagy a szinkron megszólalt. */
  get eredmenyCsoportok(): any[] {
    return (this.eredmeny?.csoportok || [])
      .filter((c: any) => c.uj_tagsag || c.lezart_tagsag || c.szinkron);
  }

  async vegrehajt() {
    if (!this.elemzes) return;
    if (!confirm(`${this.vartUjCsoport} új csoport jön létre, és ${this.vartUjTagsag} tanuló kerül be.`
      + (this.szinkron
        ? `\n\nSZINKRON: a fájlban szereplő ${this.szinkronCsoportok} csoportból várhatóan `
          + `${this.vartLezaras} tanuló tagsága LEZÁRUL (tegnapi dátummal), mert nincs a fájlban.`
        : '')
      + '\n\nFolytatod?')) return;
    this.fut = true;
    try {
      const eredmeny: any = await firstValueFrom(this.http.post(
        `/api/import-csoporttagsag/vegrehajt/${this.intezmenyPath}`,
        { sorok: this.kuldhetoSorok, dontesek: this.dontes, szinkron: this.szinkron }));
      this.data.sendToast(
        `${eredmeny?.csoport_letrehozva || 0} új csoport, `
        + `${eredmeny?.tagsag_uj || 0} új tagság`
        + (eredmeny?.szinkron ? `, ${eredmeny?.tagsag_lezarva || 0} tagság lezárva.` : '.'), 'success');
      await this.elemez();   // az új állapot rögtön látszódjon
      /*  Az újraelemzés kinullázza az eredményt – utána tesszük vissza, különben a
       *  „Kész" blokk (és benne a lezárások csoportonként) sosem látszana. */
      this.eredmeny = eredmeny;
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az import nem sikerült.', 'error');
    } finally {
      this.fut = false;
    }
  }
}
